import React, { useState, useEffect, useRef } from 'react';
import { getSetupIntentIdFromUrlSearchParams } from '../../utils/stripeSetupIntent';
import { useNavigate } from 'react-router-dom';
import ProviderPageLayout from '../../components/ProviderPageLayout';
import PaymentSetupForm from '../../components/PaymentSetupForm';
import billingApi from '../../services/billingApi';
import { getBillingTermsSummary, formatPricePerTransaction } from '../../config/billing';
import { applyBillingProfile } from '../../utils/billingRegion';
import {
  resolveStripePublishableKey,
  getStripeKeyMode,
  stripeTestModeErrorMessage,
  isProductionAppHost,
} from '../../config/stripe';
import { getPaymentSetupReturnUrl } from '../../utils/paymentReturnUrl';
import {
  savePendingSetupIntent,
  getPendingSetupClientSecret,
} from '../../utils/pendingSetupIntent';
import { useAuth } from '../../auth/useAuth';

const ProviderPaymentSetup = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, signOut } = useAuth();
  const [clientSecret, setClientSecret] = useState('');
  const [publishableKey, setPublishableKey] = useState('');
  const [returnUrl, setReturnUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const completingRedirectRef = useRef(false);
  const initStartedRef = useRef(false);
  const [billingSummary, setBillingSummary] = useState(getBillingTermsSummary());
  const [billingProfile, setBillingProfile] = useState(null);

  const handleSignOut = async () => {
    await signOut();
    navigate('/provider/login');
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/provider/login');
      return;
    }

    const init = async () => {
      try {
        setLoading(true);
        setError('');

        const accessToken = localStorage.getItem('accessToken');
        if (!accessToken) {
          navigate('/provider/login');
          return;
        }

        const params = new URLSearchParams(window.location.search);
        const returnedSetupIntentId = getSetupIntentIdFromUrlSearchParams(params);
        const redirectStatus = params.get('redirect_status');
        const returnedClientSecret = params.get('setup_intent_client_secret');

        if (
          returnedSetupIntentId &&
          (redirectStatus === 'succeeded' || (returnedClientSecret && !redirectStatus))
        ) {
          if (completingRedirectRef.current) return;
          completingRedirectRef.current = true;
          await billingApi.completeSetup(
            accessToken,
            returnedSetupIntentId,
            returnedClientSecret || getPendingSetupClientSecret(),
            { monthlyBillingAuthorized: true }
          );
          window.history.replaceState({}, '', '/provider/payment-setup');
          navigate('/provider/');
          return;
        }

        if (returnedSetupIntentId && redirectStatus && redirectStatus !== 'succeeded') {
          setError('Payment setup was not completed. Please try again.');
          window.history.replaceState({}, '', '/provider/payment-setup');
          setLoading(false);
          return;
        }

        window.history.replaceState({}, '', '/provider/payment-setup');

        const status = await billingApi.getPaymentStatus(accessToken);
        if (status.paymentMethodOnFile) {
          navigate('/provider/');
          return;
        }

        if (initStartedRef.current) return;
        initStartedRef.current = true;

        const setup = await billingApi.createSetupIntent(accessToken);
        applyBillingProfile(setup);
        setBillingProfile({
          countryCode: setup.countryCode,
          currency: setup.currency,
          pricePerTransactionDollars: setup.pricePerTransactionDollars,
          regionLabel: setup.regionLabel,
        });
        setBillingSummary(
          getBillingTermsSummary(setup.countryCode ? { countryCode: setup.countryCode } : {})
        );

        const stripeKey = setup.stripePublishableKey || (await resolveStripePublishableKey());
        const setupReturnUrl = setup.paymentSetupReturnUrl || getPaymentSetupReturnUrl();

        if (!stripeKey) {
          setError('Stripe is not configured. Please try again later.');
          setLoading(false);
          return;
        }

        if (isProductionAppHost() && getStripeKeyMode(stripeKey) === 'test') {
          setError(stripeTestModeErrorMessage());
          setLoading(false);
          return;
        }

        if (!setup.clientSecret) {
          setError('Could not start payment setup.');
          setLoading(false);
          return;
        }

        const intentId =
          setup.setupIntentId ||
          (setup.clientSecret ? setup.clientSecret.split('_secret_')[0] : '');
        savePendingSetupIntent(intentId, setup.clientSecret);

        setPublishableKey(stripeKey);
        setReturnUrl(setupReturnUrl);
        setClientSecret(setup.clientSecret);
      } catch (err) {
        initStartedRef.current = false;
        setError(err.message || 'Unable to start payment setup.');
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [user, authLoading, navigate]);

  const handleSuccess = () => navigate('/provider/');

  if (authLoading || loading) {
    return (
      <ProviderPageLayout variant="auth">
        <div className="provider-card provider-card--auth">
          <div className="provider-loading">
            <div className="provider-spinner" />
            <span>Preparing payment setup…</span>
          </div>
        </div>
      </ProviderPageLayout>
    );
  }

  return (
    <ProviderPageLayout variant="auth">
      <div className="provider-card" style={{ maxWidth: 520 }}>
        <p className="provider-eyebrow">One last step</p>
        <h1 className="provider-title">Payment method</h1>
        <p className="provider-subtitle">
          Add your card and billing address to complete onboarding. Usage is tracked at{' '}
          <strong>
            {formatPricePerTransaction({
              currency: billingProfile?.currency,
              pricePerTransactionDollars: billingProfile?.pricePerTransactionDollars,
              countryCode: billingProfile?.countryCode,
            })}
          </strong>{' '}
          per transaction
          {billingProfile?.regionLabel ? ` (${billingProfile.regionLabel})` : ''}, combined into
          one monthly invoice.
        </p>

        <div className="provider-alert provider-alert--info">
          <strong>Billing:</strong> {billingSummary.perTransactionText}.{' '}
          {billingSummary.monthlyBillingText}
        </div>

        {error && <div className="provider-alert provider-alert--error">{error}</div>}

        {publishableKey && clientSecret && !error && (
          <PaymentSetupForm
            publishableKey={publishableKey}
            clientSecret={clientSecret}
            returnUrl={returnUrl}
            billingEmail={user?.attributes?.email || ''}
            billingName={[user?.attributes?.given_name, user?.attributes?.family_name]
              .filter(Boolean)
              .join(' ')}
            pricePerTransactionLabel={formatPricePerTransaction({
              currency: billingProfile?.currency,
              pricePerTransactionDollars: billingProfile?.pricePerTransactionDollars,
              countryCode: billingProfile?.countryCode,
            })}
            onSuccess={handleSuccess}
          />
        )}

        <div className="provider-footer-links">
          <button type="button" className="provider-link" onClick={handleSignOut}>
            Sign out
          </button>
        </div>
      </div>
    </ProviderPageLayout>
  );
};

export default ProviderPaymentSetup;
